import React, { useState, useEffect, useRef } from 'react';
import axios from '../lib/axios';
import { 
  FileSpreadsheet, 
  Link2, 
  Webhook, 
  Zap, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  Database,
  RefreshCw,
  Search,
  ChevronDown,
  Layers,
  Activity,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const InventorySync = () => {
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [spreadsheets, setSpreadsheets] = useState([]);
    const [convirtiendo, setConvirtiendo] = useState(null);
    const [avisoConversion, setAvisoConversion] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [loadingTabs, setLoadingTabs] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const [settings, setSettings] = useState({
        spreadsheet_id: '',
        spreadsheet_name: '',
        sheet_name: '',
        webhook_url: '',
        is_google_connected: false,
        last_sync_at: null
    });
    
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchSettings();
        
        // Handle click outside dropdown
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce search
    useEffect(() => {
        if (!settings.is_google_connected) return;

        const timer = setTimeout(() => {
            if (searchTerm.length >= 2 || searchTerm.length === 0) {
                fetchSpreadsheets(searchTerm);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm, settings.is_google_connected]);

    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/google/inventory-settings');
            const data = response.data;
            setSettings(prev => ({
                ...prev,
                webhook_url: data.webhook_url || '',
                is_google_connected: data.is_google_connected,
                last_sync_at: data.last_sync_at
            }));

            if (data.is_google_connected) {
                fetchSpreadsheets();
            }
        } catch (error) {
            console.error('Error fetching inventory settings:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'Nunca';
        try {
            return new Intl.DateTimeFormat('es-ES', {
                dateStyle: 'medium',
                timeStyle: 'short'
            }).format(new Date(dateString));
        } catch (e) {
            return 'Formato inválido';
        }
    };

    const fetchSpreadsheets = async (search = '') => {
        setSearching(true);
        setSpreadsheets([]); // Limpiar resultados anteriores para evitar parpadeos o desincronización
        try {
            const response = await axios.get('/api/google/spreadsheets', {
                params: { search }
            });
            setSpreadsheets(response.data);
        } catch (error) {
            console.error('Error fetching spreadsheets:', error);
            const errorMsg = error.response?.data?.error || 'Error al cargar las hojas de cálculo.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setSearching(false);
        }
    };

    const fetchTabs = async (spreadsheetId) => {
        setLoadingTabs(true);
        setTabs([]);
        try {
            const response = await axios.get(`/api/google/spreadsheets/${spreadsheetId}/tabs`);
            setTabs(response.data);
            if (response.data.length > 0) {
               setSettings(prev => ({ ...prev, sheet_name: response.data[0] }));
            }
        } catch (error) {
            console.error('Error fetching tabs:', error);
            const errorMsg = error.response?.data?.error || 'Error al cargar las pestañas de la hoja.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setLoadingTabs(false);
        }
    };

    const handleConnectGoogle = async () => {
        setConnecting(true);
        try {
            const response = await axios.get('/api/oauth/google/url');
            window.location.href = response.data.url;
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al obtener la URL de autenticación.' });
            setConnecting(false);
        }
    };

    const handleSelectSpreadsheet = (ss) => {
        setSettings(prev => ({ 
            ...prev, 
            spreadsheet_id: ss.id, 
            spreadsheet_name: ss.name,
            sheet_name: '' 
        }));
        setSearchTerm(ss.name);
        setShowDropdown(false);
        fetchTabs(ss.id);
    };

    const handleSync = async () => {
        if (!settings.spreadsheet_id || !settings.webhook_url || !settings.sheet_name) {
            setMessage({ type: 'error', text: 'Por favor complete todos los campos requeridos.' });
            return;
        }

        setSyncing(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await axios.post('/api/google/sync-inventory', {
                spreadsheet_id: settings.spreadsheet_id,
                sheet_name: settings.sheet_name,
                webhook_url: settings.webhook_url
            });
            setMessage({ type: 'success', text: response.data.message });
            fetchSettings(); // Refresh timestamp
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Error al iniciar la sincronización.';
            setMessage({ type: 'error', text: errorMsg });
        } finally {
            setSyncing(false);
        }
    };

    const esExcel = (ss) => ss.mimeType && ss.mimeType !== 'application/vnd.google-apps.spreadsheet';

    /**
     * Un .xlsx en Drive no se puede leer con la API de Sheets: ni sus pestanas
     * ni sus datos. En vez de dejar el desplegable vacio sin explicacion, se
     * ofrece convertirlo. Drive lo hace con una copia, asi que el Excel
     * original queda intacto y nace un archivo nuevo con otro id.
     */
    const convertirAHojaDeGoogle = async (ss) => {
        setConvirtiendo(ss.id);
        setAvisoConversion(null);
        try {
            const { data } = await axios.post('/api/google/convert-to-sheet', { file_id: ss.id });
            setAvisoConversion({ tipo: 'ok', texto: data.message });
            // Se selecciona sola: es el archivo que el usuario queria usar.
            await handleSelectSpreadsheet({ id: data.id, name: data.name });
        } catch (err) {
            setAvisoConversion({ tipo: 'error', texto: err?.response?.data?.error || 'No se pudo convertir el archivo.' });
        } finally {
            setConvirtiendo(null);
        }
    };

    const filteredSpreadsheets = spreadsheets.filter(ss => 
        ss.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4">
                <Loader2 className="animate-spin text-primary" size={40} />
                <p className="text-slate-500 dark:text-gray-400 animate-pulse font-medium">Cargando ecosistema de inventario...</p>
            </div>
        </div>
    );

    return (
        <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-8 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 blur-[100px] -mr-32 -mt-32 rounded-full"></div>
                <div className="relative z-10 flex items-center gap-5">
                    <div className="p-4 bg-primary rounded-2xl shadow-lg shadow-primary/20">
                        <FileSpreadsheet className="text-white" size={32} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                            Sincronización de Inventario
                            <span className="text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full border border-blue-500/20 uppercase tracking-widest leading-none font-bold">Smart Sync</span>
                        </h1>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                            <p className="text-slate-500 dark:text-gray-400 font-medium">Actualiza tu CRM con datos de Google Sheets</p>
                            {settings.last_sync_at && (
                                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-500 bg-indigo-500/5 px-2 py-0.5 rounded-full border border-indigo-500/10">
                                    <Activity size={10} />
                                    Última sincronización: {formatDate(settings.last_sync_at)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!settings.is_google_connected ? (
                    <button
                        onClick={handleConnectGoogle}
                        disabled={connecting}
                        className="relative z-10 px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl flex items-center justify-center gap-3 font-black tracking-widest uppercase text-sm transition-all shadow-lg hover:scale-105 active:scale-95"
                    >
                        {connecting ? <Loader2 className="animate-spin" size={20} /> : <Link2 size={18} />}
                        Conectar Google
                    </button>
                ) : (
                    <div className="flex items-center gap-2 bg-green-500/10 text-green-600 px-4 py-2 rounded-xl border border-green-500/20 font-bold text-sm">
                        <CheckCircle2 size={16} /> Google Conectado
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Form Section */}
                <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-500/10 rounded-lg text-blue-600">
                            <Database size={20} />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Datos de Origen</h2>
                    </div>

                    <div className="space-y-6">
                        {/* Spreadsheet Searchable Select */}
                        <div className="space-y-2 relative" ref={dropdownRef}>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Search size={12} /> Buscar Hoja de Cálculo
                            </label>
                            <div className="relative">
                                <input 
                                    type="text"
                                    placeholder={settings.is_google_connected ? "Escribe para buscar..." : "Conecta Google para buscar"}
                                    disabled={!settings.is_google_connected}
                                    value={searchTerm}
                                    onFocus={() => setShowDropdown(true)}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowDropdown(true);
                                    }}
                                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-medium pr-12"
                                />
                                {searching ? (
                                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-primary animate-spin" size={20} />
                                ) : (
                                    <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform ${showDropdown ? 'rotate-180' : ''}`} size={20} />
                                )}
                            </div>

                            <AnimatePresence>
                                {showDropdown && settings.is_google_connected && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute z-50 w-full mt-2 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto"
                                    >
                                        {searching ? (
                                            <div className="p-8 text-center flex flex-col items-center gap-3">
                                                <Loader2 className="animate-spin text-primary/50" size={24} />
                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Buscando en Google...</span>
                                            </div>
                                        ) : filteredSpreadsheets.length > 0 ? (
                                            filteredSpreadsheets.map(ss => (
                                                <button
                                                    key={ss.id}
                                                    onClick={() => esExcel(ss) ? convertirAHojaDeGoogle(ss) : handleSelectSpreadsheet(ss)}
                                                    disabled={convirtiendo === ss.id}
                                                    className="w-full text-left px-5 py-4 hover:bg-primary/5 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/5 last:border-0 flex items-center justify-between group disabled:opacity-60"
                                                >
                                                    <span className="min-w-0">
                                                        <span className="block font-bold text-sm text-slate-700 dark:text-slate-200 group-hover:text-primary transition-colors truncate">{ss.name}</span>
                                                        {esExcel(ss) && (
                                                            <span className="block text-[11px] font-semibold text-amber-600 dark:text-amber-400 mt-0.5">
                                                                {convirtiendo === ss.id
                                                                    ? 'Convirtiendo...'
                                                                    : 'Es un Excel — clic para convertirlo a hoja de Google'}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {convirtiendo === ss.id
                                                        ? <Loader2 size={14} className="animate-spin text-primary shrink-0" />
                                                        : <ArrowRight size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0 shrink-0" />}
                                                </button>
                                            ))
                                        ) : (
                                            <div className="p-8 text-center text-slate-400 text-sm font-medium">
                                                {searchTerm.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron resultados'}
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {avisoConversion && (
                            <div className={`rounded-xl px-4 py-3 text-xs font-semibold leading-relaxed ${
                                avisoConversion.tipo === 'ok'
                                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                            }`}>
                                {avisoConversion.texto}
                            </div>
                        )}

                        {/* Tab Selector */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Layers size={12} /> Seleccionar Pestaña
                            </label>
                            <div className="relative">
                                <select 
                                    value={settings.sheet_name}
                                    disabled={!settings.spreadsheet_id || loadingTabs}
                                    onChange={(e) => setSettings({...settings, sheet_name: e.target.value})}
                                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-bold appearance-none cursor-pointer"
                                >
                                    <option value="" disabled>{loadingTabs ? 'Cargando pestañas...' : 'Seleccione una pestaña'}</option>
                                    {tabs.map(tab => (
                                        <option key={tab} value={tab}>{tab}</option>
                                    ))}
                                </select>
                                {loadingTabs ? (
                                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-primary animate-spin" size={20} />
                                ) : (
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={20} />
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Webhook & Sync Section */}
                <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 p-8 rounded-[2rem] shadow-lg space-y-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-500/10 rounded-lg text-purple-600">
                            <Webhook size={20} />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Configuración de Automatización</h2>
                    </div>

                    <div className="space-y-4">
                         <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Webhook de Sincronización</label>
                            <div className="relative">
                                <input 
                                    type="text"
                                    placeholder="https://servidor.com/webhook/..."
                                    value={settings.webhook_url || ''}
                                    onChange={(e) => setSettings({...settings, webhook_url: e.target.value})}
                                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-5 py-4 pl-12 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-medium"
                                />
                                <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                onClick={handleSync}
                                disabled={syncing || !settings.is_google_connected || !settings.spreadsheet_id}
                                className={`w-full py-5 rounded-2xl flex items-center justify-center gap-3 font-black tracking-widest uppercase text-base transition-all shadow-xl ${
                                    syncing || !settings.is_google_connected || !settings.spreadsheet_id
                                    ? 'bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-gray-600 cursor-not-allowed'
                                    : 'bg-primary text-white hover:scale-[1.02] active:scale-[0.98] shadow-primary/30'
                                }`}
                            >
                                {syncing ? <Loader2 className="animate-spin" size={24} /> : <RefreshCw size={22} />}
                                {syncing ? 'Sincronizando...' : 'Iniciar Sincronización'}
                            </button>
                            
                            {!settings.is_google_connected && (
                                <p className="mt-4 text-center text-[11px] text-red-500 font-bold uppercase flex items-center justify-center gap-2">
                                    <AlertCircle size={14} /> Debe conectar su cuenta de Google primero
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-100 dark:border-white/5 mt-4">
                         <div className="flex items-start gap-4">
                            <div className="p-2 bg-yellow-500/10 rounded-lg text-yellow-600 mt-1">
                                <Zap size={18} fill="currentColor" />
                            </div>
                            <div>
                                <p className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-tight">¿Cómo funciona?</p>
                                <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-1 leading-relaxed font-medium">
                                    Al sincronizar, enviamos de forma segura el token y los metadatos al webhook configurado. Tu servicio externo usará estos datos para extraer el inventario y actualizar el CRM.
                                </p>
                            </div>
                         </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {message.text && (
                <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`p-6 rounded-[2rem] text-center text-sm font-bold shadow-lg border flex items-center justify-center gap-3 ${
                        message.type === 'success' 
                        ? 'bg-green-500 text-white border-green-600' 
                        : 'bg-red-500 text-white border-red-600'
                    }`}
                >
                    {message.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                    {message.text}
                </motion.div>
            )}
        </div>
    );
};

export default InventorySync;
